/**
 * The Firebase app, auth and storage, opened once.
 *
 * Firestore lives in `data/firestore.ts` for the same reason it does in the Studio: opening
 * it means naming the database (`studio`, never `(default)`), and that belongs with the data
 * layer's conventions rather than beside `getAuth`.
 */

import { getApp, getApps, initializeApp } from 'firebase/app';
import type { FirebaseApp } from 'firebase/app';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import type { Auth } from 'firebase/auth';
import { connectStorageEmulator, getStorage } from 'firebase/storage';
import type { FirebaseStorage } from 'firebase/storage';

import { adminEnv, EMULATOR } from './env.js';

let cached: { app: FirebaseApp; auth: Auth } | null = null;
let cachedStorage: FirebaseStorage | null = null;

export function firebaseApp(): FirebaseApp {
  const env = adminEnv();
  return getApps().length > 0
    ? getApp()
    : initializeApp({
        apiKey: env.apiKey,
        authDomain: env.authDomain,
        projectId: env.projectId,
        storageBucket: env.storageBucket,
        messagingSenderId: env.messagingSenderId,
        appId: env.appId,
      });
}

export function firebase(): { app: FirebaseApp; auth: Auth } {
  if (cached === null) {
    const app = firebaseApp();
    const auth = getAuth(app);
    if (adminEnv().useEmulator) {
      connectAuthEmulator(auth, EMULATOR.authUrl, { disableWarnings: true });
    }
    cached = { app, auth };
  }
  return cached;
}

/**
 * Storage — where the catalog illustrations go.
 *
 * The **only** Storage tree this app touches is `mediums/**`, which the rules make public to
 * read and admin-only to write. It never reaches into an author's tree, and the claim would
 * not open one if it tried (`storage.rules`).
 */
export function storage(): FirebaseStorage {
  if (cachedStorage === null) {
    cachedStorage = getStorage(firebaseApp());
    if (adminEnv().useEmulator) {
      connectStorageEmulator(cachedStorage, EMULATOR.storageHost, EMULATOR.storagePort);
    }
  }
  return cachedStorage;
}
